<template>
  <Transition name="fade">
    <div v-if="show" class="modal-wrapper" @click.self="closeModal()">
      <div class="auth-modal">
        <div class="auth-modal__close" @click="closeModal()">
          <Icons variant="close" />
        </div>
        <div v-if="waiting" class="auth-modal-section auth-modal_pending" role="status" aria-live="polite">
          <p class="auth-modal__title">{{ $t(phase === 'checking' ? 'authModal.checkingTitle' : phase === 'verifying' ? 'authModal.verifyingTitle' : 'authModal.pendingTitle') }}</p>
          <p class="auth-modal__subtitle">{{ $t(phase === 'checking' ? 'authModal.checkingSubtitle' : 'authModal.pendingSubtitle') }}<br>{{ phone }}</p>
        </div>
        <div v-else-if="!codeSended && !authSuccess" class="auth-modal-section auth-modal_phone">
          <div v-if="authBanner || isKiosk">
            <img :src="isKiosk ? '/img/auth-coins-kiosk.webp' : '/img/auth-coins-tablets.webp'" class="auth-modal__promo-image" alt="">
            <p class="auth-modal__title banner">
              {{ $t('authModal.phoneTitleBanner') }}
            </p>
          </div>
          <p v-else class="auth-modal__title">
            {{ $t('authModal.phoneTitle') }}
          </p>
          <input
            v-model="phone" v-mask="'+7 ### ### ## ##'" type="tel" class="input"
            :class="{ 'input_error': phoneError }" :placeholder="phonePlaceholder" readonly
          >
          <NumberKeyboard v-model="phone" />
        </div>

        <div v-else-if="codeSended && !authSuccess" class="auth-modal-section auth-modal_code">
          <p class="auth-modal__title">
            {{ $t('authModal.codeTitle') }}
          </p>
          <span class="auth-modal__subtitle">
            {{ $t('authModal.codeSubtitle') }} <br>{{ phone }}
          </span>
<div class="auth-modal-code" :class="{ 'auth-modal-code_error': codeError }">
              <div class="auth-modal-code__item">
                <span v-if="code.length <= 0"></span>
                <p v-else>{{ code[0] }}</p>
              </div>
              <div class="auth-modal-code__item">
                <span v-if="code.length <= 1"></span>
                <p v-else>{{ code[1] }}</p>
              </div>
              <div class="auth-modal-code__item">
                <span v-if="code.length <= 2"></span>
                <p v-else>{{ code[2] }}</p>
              </div>
              <div class="auth-modal-code__item">
                <span v-if="code.length <= 3"></span>
                <p v-else>{{ code[3] }}</p>
              </div>
            </div>
            <span v-if="codeError" class="error">{{ codeError }}</span>
            <NumberKeyboard v-model="code" :inert="submitting" />
        </div>

        <div v-else-if="authSuccess" class="auth-modal-section">
          <p v-if="newUser" class="auth-modal__title">
            {{ $t('authModal.newNameTitle') }}
          </p>
          <p v-else class="auth-modal__title">
            {{ $t('authModal.nameTitle') }}
          </p>
          <span v-if="newUser" class="auth-modal__subtitle" v-html="$t('authModal.newNameSubtitle')"></span>
          <span v-else class="auth-modal__subtitle" v-html="$t('authModal.nameSubtitle')"></span>
          <input v-model="newName" type="text" class="input input_name" :class="{ 'input_error': nameError }" :placeholder="$t('global.name')" readonly>
          <span v-if="nameError" class="error">Недопустимое имя!</span>
          <Keyboard ref="keyboard" v-model="newName" :auto-show="true" />
        </div>

        <p v-if="authError" class="error" role="alert">{{ authError }}</p>
        <div class="row">
          <div v-if="phase === 'phone'" class="btn btn_outlined" @click="prevStep()">
            {{ $t('global.skip') }}
          </div>
          <div v-if="waiting || codeSended" class="btn btn_outlined" @click="prevStep()">
            {{ $t('global.back') }}
          </div>
          <div v-if="!waiting" class="btn" :class="{ disabled }" @click="nextStep()">
            {{ $t('global.continue') }}
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Icons from '@/components/UI/Icons/';
import NumberKeyboard from '@/components/UI/NumberKeyboard/'
import Keyboard from '@/components/UI/Keyboard.vue'
import { productProfile, isKiosk } from '@/config/productProfile';

import { useTerminalStore } from '@/stores';
import { captureMessage } from '@/utils/sentry'
import { usePhoneAuth } from '@/composables/usePhoneAuth'

const terminalStore = useTerminalStore();
const { t } = useI18n();

const {
    phone, code, codeSended, codeError, authSuccess, waiting, phase,
    error: authError, submitting, canContinue,
    startPhoneLogin, submitCode, reset: resetAuth, returningUser,
} = usePhoneAuth(t);
const phoneError = computed(() => Boolean(authError.value));
const newUser = ref(false);
const newName = ref('');
const nameError = ref(false);
const nameSaving = ref(false);
let nameRequest: AbortController | null = null;
const disabled = computed(() => nameSaving.value || (authSuccess.value ? newName.value.trim().length < 2 : !canContinue.value));

const show = computed(() => terminalStore.authShow);

const authBanner = computed(() => terminalStore.authBanner);

const profile = computed(() => terminalStore.profile);

const phonePlaceholder = computed(() => isKiosk ? '+7 (___) ___ __ __' : '+7');

function setAuth(bool: boolean) {
    terminalStore.setAuth(bool);
}

function setAuthBanner(bool: boolean) {
    terminalStore.setAuthBanner(bool);
}

function setProfileData(name: string, signal: AbortSignal) {
    return terminalStore.setProfileData(name, signal);
}

function closeModal() {
    nameRequest?.abort();
    nameRequest = null;
    nameSaving.value = false;
    resetAuth();
    phone.value = '';
    code.value = '';
    codeError.value = '';
    newUser.value = false;

    setAuth(false);
    if (productProfile.authBanner) {
        setAuthBanner(false);
    }
}

function nextStep() {
    if (disabled.value) { return; }
    if (authSuccess.value) {
        setName();
    } else if (codeSended.value) {
        void submitCode();
    } else {
        void startPhoneLogin();
    }
}

function setName() {
    if (nameSaving.value) { return; }
    if (newName.value.length < 2) {
        nameError.value = true;
    } else {
        const request = new AbortController();
        nameRequest = request;
        nameSaving.value = true;
        setProfileData(newName.value, request.signal)
            .then((result) => {
                if (request.signal.aborted) { return; }
                const r = result as { user?: unknown; first_name?: unknown; message?: string } | undefined;
                if (r?.user) {
                    closeModal();
                } else {
                    if (r?.first_name) {
                        nameError.value = true;
                    } else {
                        alert('Ошибка! Имя не сохранено: ' + r?.message);
                        captureMessage('Set name error: ' + r?.message, {
                            tags: { module: 'auth', action: 'setName' },
                        });
                    }
                }
            })
            .catch(() => {
                if (!request.signal.aborted) { nameError.value = true; }
            })
            .finally(() => {
                if (nameRequest === request) {
                    nameRequest = null;
                    nameSaving.value = false;
                }
            });
    }
}

function prevStep() {
    if (phase.value === 'phone') { closeModal(); } else { resetAuth(); }
}

watch(phone, () => { authError.value = ''; });
watch(show, (visible) => {
    if (!visible) { resetAuth(); }
}, { flush: 'sync' });
watch(authSuccess, (success) => {
    if (success && returningUser.value) {
        closeModal();
    } else if (success) {
        newUser.value = !profile.value?.first_name;
    }
});

watch(code, (newValue) => {
    if (newValue.length > 4) {
        code.value = newValue.slice(0, 4);
    }
    if (codeError.value) {
        codeError.value = '';
    }
}, { flush: 'sync' });

watch(profile, (newValue) => {
    if (newValue) {
        newName.value = newValue.first_name ?? '';
    }
});

watch(newName, () => {
    if (nameError.value) {
        nameError.value = false;
    }
});
</script>
<style scoped lang="scss">
.auth-modal {
    position: relative;
    padding: 48px 32px 32px 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 80dvw;
    background: var(--color-white);
    border-radius: 30px;

    [data-product="kiosk"] & {
        max-height: calc(100dvh - 32px);
        overflow-y: auto;
    }

    &-section img.auth-modal__promo-image {
        display: block;
        width: min(440px, 60dvw);
        height: auto;
        max-height: 30dvh;
        margin: 0 auto 16px;
        object-fit: contain;
    }

    & .auth-modal_pending {
        min-height: 260px;
        padding: 48px 16px;
        justify-content: center;
        text-align: center;
    }

    &__close {
        position: absolute;
        top: 32px;
        right: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 72px;
        height: 72px;
        border: 2px solid #C6C6C6;
        border-radius: 50%;
    }

    &-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        & span {
            display: flex;
            flex-direction: column;
            align-items: center;
        }
        & img {
            height: 100%;
        }
    }

    &__title {
        margin-bottom: 8px;
        font-size: 28px;
        font-weight: 700;
    }

    &__subtitle {
        margin-bottom: 28px;
        font-size: 18px;
        font-weight: 400;
        text-align: center;
        white-space: pre-line;
    }

    & .input {
        height: 80px;
        text-align: center;
        font-size: 28px;
        font-weight: 700;
        color: var(--color-green);
        border: 2px solid var(--color-light-grey);
        border-radius: 15px;
        outline: none;

        &::placeholder {
            color: var(--color-grey);
        }

        &_name {
            margin-top: 50px;
            text-transform: capitalize;

            &::placeholder {
                font-weight: 400;
                color: var(--color-grey);
            }
        }

        &_error {
            color: var(--color-red);
        }
    }

    &-section &-code {
        margin-top: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 38px;
        height: 48px;
        font-size: 22px;
        font-weight: 700;

        &_error {
            color: var(--color-red);
        }

        &__item span {
            display: block;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: var(--color-grey);
        }
    }

    & .number-keyboard {
        margin-top: 48px;
    }

    & .row {
        margin-top: 72px;
        display: flex;
        align-items: center;
        gap: 16px;
        width: 100%;
    }

    & .error {
        margin-top: 12px;
        color: var(--color-red);
        font-weight: 600;
    }

    & .btn.disabled {
        color: var(--color-green);
        background: #D3F3EA;
        cursor: not-allowed;
    }
}
</style>
